import { z } from "zod";
import { ColumnSchema, ViewSchema, ViewType } from "./views";

export enum InvoiceColumn {
    DATE = "date",
    INVOICE_DATE = "invoiceDate",
    STATUS = "status",
    EXTERNAL_STATUS = "externalStatus",
    INVOICE_NUMBER = "invoiceNumber",
    CUSTOMER = "customer",
    ACCOUNT = "account",
    DUE_DATE = "dueDate",
    TERMS = "terms",
    SENT_AT = "sentAt",
    SENT_BY = "sentBy",
    AMOUNT = "amount",
    OPEN = "open",
    PAID = "paid",
    DAYS_OVERDUE = "daysOverdue",
    ENTITY = "entity",
    EXTERNAL_LINK = "externalLink",
    SELECT = "select",
    SCHEDULE = "schedule",
    MATCH = "match",
    LOCK = "lock",
}

export enum InvoicesByCustomerColumn {
    CUSTOMER = "customer",
    CURRENT = "current",
    PAST_DUE_1_TO_30 = "pastDue1To30",
    PAST_DUE_31_TO_60 = "pastDue31To60",
    PAST_DUE_61_TO_90 = "pastDue61To90",
    PAST_DUE_91_PLUS = "pastDue91Plus",
    TOTAL_OPEN = "totalOpen",
    TOTAL_PAID = "totalPaid",
    INVOICES_COUNT = "invoicesCount",
    ENTITIES = "entities",
}

export enum InvoiceLineItemColumn {
    DATE = "date",
    STATUS = "status",
    EXTERNAL_STATUS = "externalStatus",
    ITEM = "item",
    KIND = "kind",
    DESCRIPTION = "description",
    CLASS = "class",
    CUSTOMER = "customer",
    ACCOUNT = "account",
    ACCRUAL_ACCOUNT = "accrualAccount",
    ACCOUNTS_RECEIVABLE = "accountsReceivable",
    DUE_DATE = "dueDate",
    AMOUNT = "amount",
    OPEN = "open",
    ENTITY = "entity",
    EXTERNAL_LINK = "externalLink",
    INVOICE_NUMBER = "invoiceNumber",
    SCHEDULE = "schedule",
    MATCH = "match",
    LOCK = "lock",
}

export const InvoiceViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.INVOICE),
    columns: z.array(
        ColumnSchema.extend({
            id: z.nativeEnum(InvoiceColumn),
        }),
    ),
});

const LEGACY_INVOICES_BY_CUSTOMER_COLUMN_MIGRATIONS: Record<
    string,
    readonly InvoicesByCustomerColumn[]
> = {
    notYetDue: [InvoicesByCustomerColumn.CURRENT],
    pastDue61Plus: [
        InvoicesByCustomerColumn.PAST_DUE_61_TO_90,
        InvoicesByCustomerColumn.PAST_DUE_91_PLUS,
    ],
};

/**
 * Translates the pre-PRO-12246 aging column ids (`notYetDue`, `pastDue61Plus`)
 * to the current 5-bucket scheme so cached or server-saved customer layouts
 * keep their columns instead of silently resetting to the default. `notYetDue`
 * becomes `current`; the old combined `pastDue61Plus` amount is split into
 * `pastDue61To90` and `pastDue91Plus`. Idempotent for already-migrated layouts.
 */
export const migrateInvoicesByCustomerColumns = <T extends { id: string }>(
    columns: readonly T[],
): T[] => {
    const seenIds = new Set<string>();
    return columns.flatMap((column) => {
        const replacementIds =
            LEGACY_INVOICES_BY_CUSTOMER_COLUMN_MIGRATIONS[column.id];
        const nextColumns = replacementIds
            ? replacementIds.map((id) => ({ ...column, id }))
            : [column];
        return nextColumns.filter((nextColumn) => {
            if (seenIds.has(nextColumn.id)) {
                return false;
            }
            seenIds.add(nextColumn.id);
            return true;
        });
    });
};

export const InvoicesByCustomerViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.INVOICES_BY_CUSTOMER),
    columns: z.preprocess(
        (value) =>
            Array.isArray(value)
                ? migrateInvoicesByCustomerColumns(value)
                : value,
        z.array(
            ColumnSchema.extend({
                id: z.nativeEnum(InvoicesByCustomerColumn),
            }),
        ),
    ),
});

export const InvoiceLineItemViewSchema = ViewSchema.extend({
    type: z.literal(ViewType.INVOICE_LINE_ITEM),
    columns: z.array(
        ColumnSchema.extend({
            id: z.union([
                z.nativeEnum(InvoiceLineItemColumn),
                z.string().startsWith("ledgerAccount:"),
                z.string().startsWith("ledgerReceivable:"),
            ]),
        }),
    ),
});

export type InvoiceView = z.infer<typeof InvoiceViewSchema>;
export type InvoicesByCustomerView = z.infer<
    typeof InvoicesByCustomerViewSchema
>;
export type InvoiceLineItemView = z.infer<typeof InvoiceLineItemViewSchema>;
